import { cn } from "../lib/utils";

export const Logo = ({ collapsed = false, className }) => {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/* Logo Icon - Always visible */}
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary via-primary-glow to-chart-2 opacity-20 blur-sm animate-pulse"></div>
        
        {/* Main logo container */}
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-9 w-9 relative z-10"
        >
          {/* Background hexagon */}
          <path
            d="M20 2L36 11V29L20 38L4 29V11L20 2Z"
            fill="url(#logoGradient)"
            stroke="url(#logoStroke)"
            strokeWidth="1.5"
          />
          
          {/* Inner network nodes */}
          <circle cx="20" cy="12" r="3" fill="hsl(var(--primary-foreground))" fillOpacity="0.9" />
          <circle cx="12" cy="24" r="2.5" fill="hsl(var(--primary-foreground))" fillOpacity="0.7" />
          <circle cx="28" cy="24" r="2.5" fill="hsl(var(--primary-foreground))" fillOpacity="0.7" />
          <circle cx="20" cy="28" r="2" fill="hsl(var(--primary-foreground))" fillOpacity="0.5" />
          
          {/* Connection lines */}
          <path
            d="M20 12L12 24M20 12L28 24M12 24L20 28M28 24L20 28M12 24L28 24"
            stroke="hsl(var(--primary-foreground))"
            strokeWidth="1"
            strokeOpacity="0.6"
            strokeLinecap="round"
          />
          
          {/* Center pulse dot */}
          <circle cx="20" cy="20" r="1.5" fill="hsl(var(--primary-foreground))">
            <animate
              attributeName="r"
              values="1.5;2.5;1.5"
              dur="2s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="1;0.5;1"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
          
          {/* Gradients */}
          <defs>
            <linearGradient id="logoGradient" x1="4" y1="2" x2="36" y2="38" gradientUnits="userSpaceOnUse">
              <stop stopColor="hsl(var(--primary))" />
              <stop offset="0.5" stopColor="hsl(var(--primary-glow))" />
              <stop offset="1" stopColor="hsl(var(--chart-2))" />
            </linearGradient>
            <linearGradient id="logoStroke" x1="4" y1="2" x2="36" y2="38" gradientUnits="userSpaceOnUse">
              <stop stopColor="hsl(var(--primary-glow))" />
              <stop offset="1" stopColor="hsl(var(--primary))" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Logo Text - Hidden when collapsed */}
      {!collapsed && (
        <div className="flex flex-col">
          <span className="text-lg font-bold tracking-tight text-sidebar-foreground leading-none">
            Data<span className="text-sidebar-accent">Forge</span>
          </span>
          <span className="text-[10px] text-sidebar-foreground/50 tracking-widest uppercase">
            Analytics
          </span>
        </div>
      )}
    </div>
  );
};

// Compact version for very small spaces
export const LogoIcon = ({ className, size = "md" }) => {
  const sizes = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-10 w-10",
    xl: "h-12 w-12",
  };

  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(sizes[size], className)}
    >
      <path
        d="M20 2L36 11V29L20 38L4 29V11L20 2Z"
        fill="url(#logoGradientIcon)"
        stroke="url(#logoStrokeIcon)"
        strokeWidth="1.5"
      />
      <circle cx="20" cy="12" r="3" fill="white" fillOpacity="0.9" />
      <circle cx="12" cy="24" r="2.5" fill="white" fillOpacity="0.7" />
      <circle cx="28" cy="24" r="2.5" fill="white" fillOpacity="0.7" />
      <circle cx="20" cy="28" r="2" fill="white" fillOpacity="0.5" />
      <path
        d="M20 12L12 24M20 12L28 24M12 24L20 28M28 24L20 28M12 24L28 24"
        stroke="white"
        strokeWidth="1"
        strokeOpacity="0.6"
        strokeLinecap="round"
      />
      <circle cx="20" cy="20" r="1.5" fill="white" />
      <defs>
        <linearGradient id="logoGradientIcon" x1="4" y1="2" x2="36" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(182, 72%, 38%)" />
          <stop offset="0.5" stopColor="hsl(182, 80%, 50%)" />
          <stop offset="1" stopColor="hsl(192, 75%, 50%)" />
        </linearGradient>
        <linearGradient id="logoStrokeIcon" x1="4" y1="2" x2="36" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(182, 80%, 50%)" />
          <stop offset="1" stopColor="hsl(182, 72%, 38%)" />
        </linearGradient>
      </defs>
    </svg>
  );
};
